// src/webideal-recruit/pages/Jobs.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { createRecruitUrl } from "../Layout";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { backendApi } from "@/api/backendApi";

import { motion, AnimatePresence } from "framer-motion";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Tabs,
  TabsList,
  TabsTrigger
} from "@/components/ui/tabs";

import { 
  Plus,
  Search,
  Filter,
  LayoutGrid,
  List
} from "lucide-react";

import JobCard from "../components/jobs/JobCard";

import { Skeleton } from "@/components/ui/skeleton";


export default function Jobs() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState("grid");

  const queryClient = useQueryClient();

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ["jobs"],
    queryFn: () => backendApi.entities.Job.list("-created_date"),
  });

  const { data: applications = [] } = useQuery({
    queryKey: ["applications"],
    queryFn: () => backendApi.entities.Application.list(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => backendApi.entities.Job.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["jobs"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => backendApi.entities.Job.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["jobs"] }),
  });

  const createMutation = useMutation({
    mutationFn: (data) => backendApi.entities.Job.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["jobs"] }),
  });


  const handleStatusChange = (id, status) => {
    updateMutation.mutate({ id, data: { status } });
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this job?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleDuplicate = (job) => {
    const { id, created_date, updated_date, created_by, ...newJob } = job;
    createMutation.mutate({
      ...newJob,
      title: `${job.title} (Copy)`,
      status: "draft",
    });
  };

  const getApplicantCount = (id) => {
    return applications.filter((x) => x.job_id === id).length;
  };

  const filteredJobs = jobs.filter((job) => {
    const matchesSearch =
      job.title.toLowerCase().includes(search.toLowerCase()) ||
      job.department?.toLowerCase().includes(search.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || job.status === statusFilter;

    return matchesSearch && matchesStatus && !job.is_template;
  });

  const templates = jobs.filter((job) => job.is_template);


  // ------------------------------------------------------------
  // LOADING UI
  // ------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="space-y-10">
        <div className="flex justify-between items-center">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-40" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-64 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }


  // ------------------------------------------------------------
  // MAIN UI (Matches Screenshot Exactly)
  // ------------------------------------------------------------
  return (
    <div className="space-y-10">

      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: -15 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Jobs</h1>
          <p className="text-slate-500 mt-1">
            Manage your job postings and templates
          </p>
        </div>

        <Link to={createRecruitUrl("CreateJob")}>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-md">
            <Plus className="w-4 h-4 mr-2" />
            Post New Job
          </Button>
        </Link>
      </motion.div>


      {/* FILTERS BAR */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row gap-4"
      >
        {/* Search */}
        <div className="relative flex-1 max-w-lg">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search jobs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white border-slate-200 rounded-xl"
          />
        </div>

        {/* Status Filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-white border-slate-200 rounded-xl">
            <Filter className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>

        {/* Grid / List Toggle */}
        <div className="hidden lg:flex bg-white border border-slate-200 rounded-xl p-1">
          <Button
            size="icon"
            variant={viewMode === "grid" ? "secondary" : "ghost"}
            onClick={() => setViewMode("grid")}
            className="rounded-lg"
          >
            <LayoutGrid className="w-4 h-4" />
          </Button>
          <Button
            size="icon"
            variant={viewMode === "list" ? "secondary" : "ghost"}
            onClick={() => setViewMode("list")}
            className="rounded-lg"
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </motion.div>


      {/* TABS (Matches screenshot exactly) */}
      <Tabs defaultValue="jobs" className="w-full">
        <TabsList className="bg-slate-100 rounded-xl p-1">
          <TabsTrigger value="jobs" className="rounded-lg">
            All Jobs ({filteredJobs.length})
          </TabsTrigger>

          <TabsTrigger value="templates" className="rounded-lg">
            Templates ({templates.length})
          </TabsTrigger>
        </TabsList>
      </Tabs>


      {/* JOBS GRID */}
      <AnimatePresence mode="wait">
        {filteredJobs.length === 0 ? (
          <div className="py-20 text-center text-slate-500">
            No jobs found
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={
              viewMode === "grid"
                ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                : "space-y-4"
            }
          >
            {filteredJobs.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                applicantCount={getApplicantCount(job.id)}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
                onDuplicate={handleDuplicate}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
